import { useMemo, useState } from 'react'
import { useNavigate } from 'react-router'
import { EyeOff, Plus, Send, Upload } from 'lucide-react'
import type { ColumnDef } from '@tanstack/react-table'
import {
  DataTable, DateValue, ImportDialog, PageHeader, PermissionGate, StatusBadge,
} from '@/components/common'
import { Button } from '@/components/ui/button'
import { Card } from '@/components/ui/card'
import { useBreadcrumb } from '@/hooks/useBreadcrumb'
import { usePermission } from '@/hooks/usePermission'
import { toApiProblem } from '@/lib/api/client'
import {
  InviteResultsDialog, useInviteEmployees, type EmployeeInviteResult,
} from '@/features/sysadmin'
import { Select } from '@/components/ui/select'
import { useDepartments, useLegalEntities } from '@/features/org'
import { downloadEmployeeExport, useEmployeeDirectory } from '../api'
import type { EmployeeSummary } from '../types'

/**
 * The directory.
 *
 * <p>Sensitive columns — work email and phone — are off by default and turned on through the
 * table's own column chooser. A directory that opens with everybody's contact details on
 * screen is a directory somebody screenshots.
 */
export function EmployeeDirectoryPage() {
  useBreadcrumb()
  const navigate = useNavigate()

  const [page, setPage] = useState(0)
  const [size, setSize] = useState(20)
  const [sort, setSort] = useState<string | undefined>('displayName,asc')
  const [search, setSearch] = useState('')
  const [legalEntityId, setLegalEntityId] = useState('')
  const [departmentId, setDepartmentId] = useState('')
  const [status, setStatus] = useState('')
  const [importing, setImporting] = useState(false)
  const canInvite = usePermission('admin:user_manage')
  const invite = useInviteEmployees()
  const [inviteResults, setInviteResults] = useState<EmployeeInviteResult[] | null>(null)
  const [inviteError, setInviteError] = useState<string | null>(null)

  const employees = useEmployeeDirectory({
    page, size, sort, q: search, legalEntityId, departmentId, status,
  })
  const entities = useLegalEntities()
  const departments = useDepartments()

  const columns = useMemo<ColumnDef<EmployeeSummary, unknown>[]>(() => [
    {
      id: 'employeeCode',
      header: 'Code',
      accessorFn: (row) => row.employeeCode,
      cell: ({ row }) => (
        <button
          type="button"
          className="font-mono underline underline-offset-2"
          onClick={() => { void navigate(`/employees/${row.original.id}`) }}
        >
          {row.original.employeeCode}
        </button>
      ),
    },
    { id: 'displayName', header: 'Name', accessorFn: (row) => row.displayName },
    { id: 'departmentName', header: 'Department', accessorFn: (row) => row.departmentName ?? '—' },
    { id: 'designationName', header: 'Designation', accessorFn: (row) => row.designationName ?? '—' },
    { id: 'locationName', header: 'Location', accessorFn: (row) => row.locationName ?? '—' },
    { id: 'managerName', header: 'Reports to', accessorFn: (row) => row.managerName ?? '—' },
    {
      id: 'joinDate',
      header: 'Joined',
      accessorFn: (row) => row.joinDate,
      cell: ({ row }) => <DateValue value={row.original.joinDate} />,
    },
    {
      id: 'status',
      header: 'Status',
      accessorFn: (row) => row.status,
      cell: ({ row }) => (
        <StatusBadge
          status={row.original.status}
          tone={row.original.status === 'ACTIVE' ? 'success' : 'neutral'}
          label={row.original.status === 'ACTIVE' ? 'Active' : row.original.status.toLowerCase()}
        />
      ),
    },
    // Off by default: the column chooser turns them on for whoever needs them.
    { id: 'workEmail', header: 'Work email', accessorFn: (row) => row.workEmail ?? '—' },
    { id: 'workPhone', header: 'Work phone', accessorFn: (row) => row.workPhone ?? '—' },
  ], [navigate])

  return (
    <div className="space-y-4">
      <PageHeader
        title="Directory"
        subtitle="Everybody, searchable, with the contact columns off until somebody asks for them."
        actions={
          <PermissionGate permission="employee:write">
            <Button type="button" variant="outline" onClick={() => setImporting(true)}>
              <Upload className="size-4" aria-hidden="true" />
              Import
            </Button>
            <Button type="button" onClick={() => { void navigate('/employees/new') }}>
              <Plus className="size-4" aria-hidden="true" />
              Add employee
            </Button>
          </PermissionGate>
        }
      />

      <ImportDialog
        open={importing}
        onOpenChange={setImporting}
        what="employees"
        prefix="/v1/employees"
        invalidateKey={['employees']}
        createMissingOption
      />

      <InviteResultsDialog results={inviteResults} onClose={() => setInviteResults(null)} />
      {inviteError && (
        <p role="alert" className="text-sm text-[var(--color-danger)]">{inviteError}</p>
      )}

      <Card className="flex items-start gap-2 p-3 text-sm text-slate-700">
        <EyeOff className="mt-0.5 size-4 shrink-0" aria-hidden="true" />
        Work email and phone are hidden by default and can be shown from the Columns menu. A
        directory that opens with everybody's contact details on screen is one somebody
        screenshots.
      </Card>

      <DataTable<EmployeeSummary>
        columns={columns}
        data={employees.data}
        isLoading={employees.isPending}
        error={employees.error}
        onRetry={() => void employees.refetch()}
        page={page}
        size={size}
        sort={sort}
        search={search}
        onPageChange={setPage}
        onSizeChange={setSize}
        onSortChange={setSort}
        onSearchChange={(value) => { setSearch(value); setPage(0) }}
        // Exports what is on screen, not the whole table: somebody who filtered to eighty
        // people and downloaded 1,248 would not notice until they had sent it on.
        onExport={() => {
          void downloadEmployeeExport({ q: search, legalEntityId, departmentId, status })
        }}
        emptyTitle="Nobody matches"
        emptyDescription="Try a different search, or clear it to see everybody."
        initialHiddenColumns={['workEmail', 'workPhone']}
        onRowClick={(row) => { void navigate(`/employees/${row.id}`) }}
        // Selection exists for one action: inviting people to sign in, several at a time.
        // Without the permission there is nothing to do with a selection, so none is offered.
        enableSelection={canInvite}
        getRowId={(row) => row.id}
        bulkActions={(selectedIds, clear) => (
          <Button
            type="button" size="sm"
            disabled={invite.isPending}
            onClick={() => {
              setInviteError(null)
              invite.mutate({ employeeIds: selectedIds }, {
                onSuccess: (results) => { setInviteResults(results); clear() },
                onError: (caught) => setInviteError(
                  toApiProblem(caught)?.detail ?? 'The invitations could not be created.'),
              })
            }}
          >
            <Send className="size-4" aria-hidden="true" />
            Invite selected to HUMIS
          </Button>
        )}
        toolbar={
          <>
            <Select
              aria-label="Legal entity"
              value={legalEntityId}
              onChange={(event) => { setLegalEntityId(event.target.value); setPage(0) }}
            >
              <option value="">Every entity</option>
              {entities.data?.map((entity) => (
                <option key={entity.id} value={entity.id}>{entity.name}</option>
              ))}
            </Select>
            <Select
              aria-label="Department"
              value={departmentId}
              onChange={(event) => { setDepartmentId(event.target.value); setPage(0) }}
            >
              <option value="">Every department</option>
              {departments.data?.map((department) => (
                <option key={department.id} value={department.id}>{department.name}</option>
              ))}
            </Select>
            <Select
              aria-label="Status"
              value={status}
              onChange={(event) => { setStatus(event.target.value); setPage(0) }}
            >
              <option value="">Any status</option>
              <option value="PRE_JOINING">Pre-joining</option>
              <option value="PROBATION">On probation</option>
              <option value="ACTIVE">Active</option>
              <option value="NOTICE_PERIOD">On notice</option>
              <option value="SUSPENDED">Suspended</option>
              <option value="EXITED">Exited</option>
            </Select>
          </>
        }
      />
    </div>
  )
}
